import { type ReactElement, useState } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useParams } from "react-router-dom"

import { AddOutlined } from "@mui/icons-material"
import { Grid, Skeleton } from "@mui/material"
import { Button, type InputChangeEvent, InputText, Menu, MenuItem } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanEmptyState, KonanEmptyStateSearch } from "../../../components/utility/KonanEmptyState"
import { useDebounce } from "../../../hooks/useDebounce"
import { KonanAPI } from "../../../services/KonanAPI"
import type { DataFileGroupList } from "../../../types/generated/api/DataFileGroupList"
import type { PaginatedDataFileGroupListList } from "../../../types/generated/api/PaginatedDataFileGroupListList"
import { extractPageFromBackEndPaginationLink, fileExtensionValidator } from "../../../utils/genericHelpers"

import styles from "./DataFilesMenu.module.scss"

interface DataFilesMenuProps {
  onDataFileUpload: (file: File) => void
  onAddDataFile: (file: DataFileGroupList) => void
  anchorEl: HTMLElement | null
  onClose: () => void
}

export function DataFilesMenu(props: Readonly<DataFilesMenuProps>): ReactElement {
  const { onDataFileUpload, onAddDataFile, anchorEl, onClose } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("rulesets", {
    keyPrefix: "dataFilesMenu",
  })

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [searchValue, setSearchValue] = useState<string>("")

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastDataFileRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isDataFilesLoading && !isDataFilesFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  const { open: openDropZone, getInputProps } = useDropzone({
    multiple: false,
    onDropAccepted: (files) => {
      setAcceptedFiles([...acceptedFiles, ...files])
      onDataFileUpload(files[0] as File)
      onClose()
    },
    accept: {
      "text/*": [".csv"],
    },
    validator: fileExtensionValidator,
  })

  // fetch Data files
  const {
    data: dataFiles,
    isLoading: isDataFilesLoading,
    isFetching: isDataFilesFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDataFileGroupListList>, AxiosError, DataFileGroupList>(
    ["data-files", "CONDITION_LIST", projectId, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchDataFiles({
        project_uuid: projectId as string,
        page: pageParam,
        pageSize: 20,
        search: searchValue,
        type: ["CONDITION_LIST"],
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce(
          (accumulator, page) => accumulator.concat(page.data.results as Array<DataFileGroupList>),
          [] as Array<DataFileGroupList>,
        ),
      }),
      enabled: !!projectId,
    },
  )

  const onChange = (e: InputChangeEvent): void => {
    setSearchValue(e.target.value as string)
  }

  const debouncedOnChange = useDebounce(onChange, 400)

  return (
    <Menu key="data-files-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => onClose()}>
      <Grid item container justifyContent="center" paddingLeft={1} paddingRight={1}>
        {/* must add this input to make it work in safari*/}
        <input {...getInputProps()} />
        <Button variant="secondary" startIcon={<AddOutlined fontSize="small" />} onClick={openDropZone} fullWidth>
          {t("newDataFileButton")}
        </Button>
      </Grid>

      <Grid container p={1} gap={1}>
        <Grid item xs={12}>
          <InputText
            id="data-files"
            fullWidth
            placeholder={t("searchInputPlaceholder")}
            hideDescription
            handleChange={debouncedOnChange}
            type="text"
          />
        </Grid>

        <Grid item xs={12} className={styles.optionsContainer}>
          {dataFiles?.pages.map((item, index) => (
            <MenuItem
              ref={hasNextPage && index === dataFiles?.pages.length - 1 ? lastDataFileRef : undefined}
              key={item.uuid}
              onClick={() => {
                onAddDataFile(item)
                onClose()
              }}
            >
              {item.name}
            </MenuItem>
          ))}

          {(isDataFilesFetching || isDataFilesLoading || isFetchingNextPage) && (
            <MenuItem disabled>
              <Skeleton height={28} width={"60%"} />
            </MenuItem>
          )}

          {!(isDataFilesFetching || isDataFilesLoading || isFetchingNextPage) && searchValue && (
            <KonanEmptyStateSearch title={searchValue} />
          )}

          {!(isDataFilesFetching || isDataFilesLoading || isFetchingNextPage) &&
            dataFiles?.pages.length === 0 &&
            !searchValue && <KonanEmptyState title={t("emptyState.title")} subTitle={t("emptyState.subtitle")} />}
        </Grid>
      </Grid>
    </Menu>
  )
}
